import { Link } from "react-router-dom";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";

// Boutons d'appel à l'action en nombre LIBRE d'une section — rendu des blocs
// "cta_button" (voir backend blocks.py CTA_BUTTON_BLOCK). Une section n'a
// plus un nombre figé de boutons (un seul `cta_label`, ou principal +
// secondaire) : l'admin en ajoute autant qu'il veut depuis le Studio, chacun
// avec son propre libellé, lien et style. Les boutons D'ORIGINE de chaque
// section sont rendus avant ceux-ci et restent inchangés — une section sans
// aucun bloc de ce type s'affiche exactement comme avant l'introduction de
// ce composant (`ctaButtonBlocks` renvoie alors un tableau vide, et les
// sections n'ajoutent aucun conteneur).
//
// L'allure de base d'un bouton dépend du FOND sur lequel la section le pose
// (`tone`), pas de la section elle-même : "light" pour un fond clair,
// "dark" pour un fond sombre (bannière image/vidéo/promo, "Image et texte"
// en variante sombre). La couleur choisie bouton par bouton
// (`cta_color`, voir resolveButtonStyle) prend ensuite le dessus.
const TONE_CLASSNAMES = {
  light: {
    primary: "bg-ink text-white hover:opacity-90 transition-opacity",
    outline: "border-control border-stone text-ink hover:bg-ink/5 transition-colors",
  },
  dark: {
    primary: "bg-white text-ink hover:opacity-90 transition-opacity",
    outline: "border border-white/25 text-white hover:bg-white/10 transition-colors",
  },
};

// Gabarits repris des boutons DÉJÀ affichés par les sections concernées,
// pour qu'un bouton ajouté se pose à côté d'eux sans détonner : "md" =
// bouton du hero/de la bannière image, "sm" = bouton de la bannière promo,
// "caps" = petites capitales espacées de la section "Image et texte".
const SIZE_CLASSNAMES = {
  md: "px-7 py-3.5 text-[15px]",
  sm: "px-6 py-3 text-[14px]",
  caps: "px-7 py-3.5 text-[13px] uppercase tracking-[0.1em]",
};

/** Blocs "cta_button" affichables d'une section, dans l'ordre du Studio —
 * exporté à part pour que la section sache s'il y en a AVANT de décider de
 * rendre (ou non) la rangée qui les contient. */
export function ctaButtonBlocks(blocks) {
  return (blocks || []).filter((b) => b.type === "cta_button" && b.enabled !== false);
}

function CtaButton({ block, tone, size }) {
  const props = block.props || {};
  // Un bouton tout juste ajouté n'a encore aucun libellé : il s'affiche
  // quand même (sous un nom générique) plutôt que de rester invisible dans
  // l'aperçu du Studio le temps d'être renseigné.
  const label = props.label || "Bouton";
  const url = props.url || "#";
  const outline = props.style === "outline";
  const resolved = resolveButtonStyle(props, "cta");
  // Un bouton "contour" garde son fond transparent : la couleur choisie
  // recolore son trait et son texte (même règle que le bouton secondaire de
  // la bannière promo), sinon il deviendrait un second bouton plein.
  const style = { ...resolved.style };
  if (outline && style.backgroundColor) {
    style.borderColor = style.backgroundColor;
    style.color = style.backgroundColor;
    delete style.backgroundColor;
  }
  const shared = {
    "data-block-key": block.id,
    className: [
      "inline-flex items-center gap-2 font-semibold",
      SIZE_CLASSNAMES[size] || SIZE_CLASSNAMES.md,
      (TONE_CLASSNAMES[tone] || TONE_CLASSNAMES.light)[outline ? "outline" : "primary"],
      resolved.className,
    ].join(" "),
    style,
    "aria-label": !resolved.showText ? label : undefined,
  };
  const content = (
    <>
      {resolved.showIcon && resolved.Icon && <resolved.Icon className="h-4 w-4" />}
      {resolved.showText && label}
    </>
  );
  // Lien interne ("/boutique") en navigation SPA, comme le bouton secondaire
  // du hero ; tout le reste (ancre "#…", "tel:", "mailto:", lien absolu) en
  // <a> classique.
  return url.startsWith("/") ? (
    <Link to={url} {...shared}>{content}</Link>
  ) : (
    <a href={url} {...shared}>{content}</a>
  );
}

export default function CtaButtonBlocks({ blocks, tone = "light", size = "md" }) {
  const buttons = ctaButtonBlocks(blocks);
  if (!buttons.length) return null;
  // Fragment (pas de conteneur propre) : chaque section place ces boutons
  // dans SA rangée, à côté de ceux qu'elle affichait déjà.
  return (
    <>
      {buttons.map((block) => (
        <CtaButton key={block.id} block={block} tone={tone} size={size} />
      ))}
    </>
  );
}
